import type {
  ManagementAttentionCandidate,
  ManagementAttentionConcentration,
} from "./forecastManagementAttention";
import {
  buildManagementAttentionShortlist,
  deduplicateManagementAttentionCandidates,
  FORECAST_MANAGEMENT_ATTENTION_CONFIG,
} from "./forecastManagementAttention";
import type {
  PipelineRiskSeverityRow,
  PipelineRiskThemeRow,
  PipelineRiskTimingRow,
  StateOfSalesV2PipelineRiskAnalysis,
  StateOfSalesV2PipelineRiskDeal,
} from "./pipelineAnalysis";
import type { PipelineRiskMovement } from "./pipelineRiskMovement";

export type PipelineRiskManagementAction =
  ManagementAttentionCandidate<StateOfSalesV2PipelineRiskDeal>;

export type PipelineRiskConcentration =
  ManagementAttentionConcentration<StateOfSalesV2PipelineRiskDeal>;

export type PipelineRiskL2Insights = {
  currentPosition: {
    exposedDealCount: number;
    exposedForecastShare: number | null;
    exposedForecastValue: number;
    exposedOpenDealValue: number;
    highRiskDealCount: number;
    highRiskForecastShare: number | null;
    highRiskForecastValue: number;
    highRiskOpenDealValue: number;
    state: "high_risk_exposure" | "no_high_risk_exposure";
  };
  drivers: {
    dominantIndicator: PipelineRiskThemeRow["indicators"][number] | null;
    dominantTheme: PipelineRiskThemeRow | null;
    state: "one_dominant_theme" | "several_equal_themes" | "no_supported_theme";
  };
  managementAttention: {
    actions: PipelineRiskManagementAction[];
    candidates: PipelineRiskManagementAction[];
    eligibleCandidates: PipelineRiskManagementAction[];
    state: "multiple_priorities" | "no_immediate_priority" | "one_priority";
    suppressedCandidateIds: string[];
  };
  movement: PipelineRiskMovement | null;
  timing: {
    dominant: PipelineRiskTimingRow | null;
    state: "one_dominant_window" | "several_equal_windows" | "timing_unavailable";
  };
};

function getDominantSeverity(
  rows: PipelineRiskSeverityRow[],
  key: PipelineRiskSeverityRow["key"],
) {
  return rows.find(row => row.key === key) ?? null;
}

function getDriverState(themes: PipelineRiskThemeRow[]) {
  if (!themes.length) return "no_supported_theme" as const;
  if (
    themes.length > 1
    && themes[0]?.rawValue === themes[1]?.rawValue
  ) {
    return "several_equal_themes" as const;
  }
  return "one_dominant_theme" as const;
}

function getTimingState(rows: PipelineRiskTimingRow[]) {
  if (!rows.length) return "timing_unavailable" as const;
  const sorted = rows.toSorted((a, b) => (
    b.rawValue - a.rawValue
    || b.forecastValue - a.forecastValue
  ));
  if (
    sorted.length > 1
    && sorted[0]?.rawValue === sorted[1]?.rawValue
  ) {
    return "several_equal_windows" as const;
  }
  return "one_dominant_window" as const;
}

const managementSeverityRank = {
  critical: 0,
  material: 1,
  informational: 2,
} as const;

function finiteNumber(value: unknown) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

export function getTopPipelineRiskConcentration(
  deals: StateOfSalesV2PipelineRiskDeal[],
  totalDealValue: number,
  key: PipelineRiskConcentration["key"],
  dimension: string,
  getLabel: (deal: StateOfSalesV2PipelineRiskDeal) => string,
): PipelineRiskConcentration | null {
  if (totalDealValue <= 0) return null;
  const groups = new Map<string, StateOfSalesV2PipelineRiskDeal[]>();
  for (const deal of deals) {
    const label = getLabel(deal).trim() || "Not specified";
    groups.set(label, [...(groups.get(label) ?? []), deal]);
  }
  if (groups.size <= 1) return null;

  const rows = Array.from(groups.entries()).map(([label, groupedDeals]) => ({
    dealCount: groupedDeals.length,
    deals: groupedDeals,
    dimension,
    key,
    label,
    share: groupedDeals.reduce(
      (sum, deal) => sum + finiteNumber(deal.value),
      0,
    ) / totalDealValue,
    value: groupedDeals.reduce(
      (sum, deal) => sum + finiteNumber(deal.value),
      0,
    ),
  }));
  const catchAllLabels = new Set(["not specified", "other", "unclassified"]);
  const specifiedRows = rows.filter(
    row => !catchAllLabels.has(row.label.toLowerCase()),
  );

  return (specifiedRows.length ? specifiedRows : rows)
    .toSorted((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .at(0) ?? null;
}

function getSeverityFromShare(
  share: number,
  material: number,
  critical: number,
) {
  if (share >= critical) return "critical" as const;
  if (share >= material) return "material" as const;
  return "informational" as const;
}

function buildPipelineRiskConcentrationCandidates(
  concentrations: PipelineRiskConcentration[],
): PipelineRiskManagementAction[] {
  return concentrations.map((concentration) => {
    const dominantDeal = concentration.deals
      .toSorted((a, b) => finiteNumber(b.value) - finiteNumber(a.value))
      .at(0);
    const dominantShare = dominantDeal && concentration.value > 0
      ? finiteNumber(dominantDeal.value) / concentration.value
      : 0;
    const dealDominated = Boolean(
      dominantDeal
      && dominantShare >= FORECAST_MANAGEMENT_ATTENTION_CONFIG
        .concentration.suppressWhenOneDealExplainsShare,
    );
    const dealIds = concentration.deals.map(deal => String(deal.id)).toSorted();
    const severity = concentration.dealCount
      >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.minimumDeals
      ? getSeverityFromShare(
        concentration.share,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.materialShareOfForecast,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.criticalShareOfForecast,
      )
      : "informational";

    return {
      id: `pipeline-risk-concentration:${concentration.key}:${concentration.label}`,
      type: "organisational_concentration",
      severity,
      entityType: concentration.key === "sales_rep" ? "rep" : concentration.key,
      entityId: concentration.label,
      dealIds,
      rootCauseKey: dealDominated && dominantDeal
        ? `deal:${dominantDeal.id}`
        : `pipeline-risk-concentration:${concentration.key}:${concentration.label}`,
      riskThesis: {
        primaryRisk: "target_dependency",
        consequence: {},
        evidence: { dealIds },
      },
      supportingMetrics: {
        dealCount: concentration.dealCount,
        dealValue: concentration.value,
        dominantDealId: dominantDeal ? String(dominantDeal.id) : "",
        dominantDealShare: dominantShare,
        shareOfDealValue: concentration.share,
        valueBasis: "deal_value",
      },
      mergedTypes: ["organisational_concentration"],
      concentration,
    };
  });
}

function buildPipelineRiskMovementCandidate(
  movement: PipelineRiskMovement | null | undefined,
  exposedOpenDealValue: number,
): PipelineRiskManagementAction | null {
  if (
    !movement?.available
    || movement.netHighRiskDealValueChange <= 0
    || movement.enteredHighRisk.count === 0
    || exposedOpenDealValue <= 0
  ) {
    return null;
  }
  const share = movement.netHighRiskDealValueChange / exposedOpenDealValue;
  const severity = getSeverityFromShare(
    share,
    FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.materialShareOfForecast,
    FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.criticalShareOfForecast,
  );
  const deals = movement.enteredHighRisk.deals;
  const dominantDeal = deals
    .toSorted((a, b) => finiteNumber(b.value) - finiteNumber(a.value))
    .at(0);
  const dominantShare = dominantDeal && movement.enteredHighRisk.dealValue > 0
    ? finiteNumber(dominantDeal.value) / movement.enteredHighRisk.dealValue
    : 0;
  const dealIds = deals.map(deal => String(deal.id)).toSorted();

  return {
    id: "pipeline-risk-movement:high-risk-exposure",
    type: "worsening_outlook",
    severity,
    entityType: dominantDeal && dominantShare >= 0.8 ? "deal" : "forecast",
    entityId: dominantDeal && dominantShare >= 0.8
      ? String(dominantDeal.id)
      : undefined,
    dealIds,
    movementImpact: movement.netHighRiskDealValueChange,
    rootCauseKey: dominantDeal && dominantShare >= 0.8
      ? `deal:${dominantDeal.id}`
      : "pipeline-risk-movement:high-risk-exposure",
    riskThesis: {
      primaryRisk: "signal_deterioration",
      consequence: {
        movementImpact: movement.netHighRiskDealValueChange,
      },
      evidence: {
        dealIds,
        riskChange: deals.length === 1 ? deals[0]?.riskChange : undefined,
      },
    },
    supportingMetrics: {
      affectedDealCount: deals.length,
      dealValue: movement.netHighRiskDealValueChange,
      shareOfDealValue: share,
      valueBasis: "deal_value",
    },
    mergedTypes: ["worsening_outlook"],
    currentRisk: dominantDeal?.currentRisk,
    dealId: dominantDeal && dominantShare >= 0.8 ? dominantDeal.id : undefined,
    dealName: dominantDeal && dominantShare >= 0.8 ? dominantDeal.name : undefined,
    previousRisk: dominantDeal?.previousRisk,
  };
}

export function buildPipelineRiskL2Insights({
  analysis,
  concentrations,
  movement,
  selectedPeriodRange,
  totalForecast,
  urgentDeals,
}: {
  analysis: StateOfSalesV2PipelineRiskAnalysis;
  concentrations: PipelineRiskConcentration[];
  movement?: PipelineRiskMovement | null;
  selectedPeriodRange?: { from: Date | string; to: Date | string } | null;
  totalForecast: number;
  urgentDeals: StateOfSalesV2PipelineRiskDeal[];
}): PipelineRiskL2Insights {
  const highRisk = getDominantSeverity(analysis.severity, "high");
  const dependencyDeals = analysis.deals
    .filter(deal => Number(deal.forecast_contribution ?? 0) > 0)
    .map(deal => ({
      ...deal,
      forecast_share: totalForecast > 0
        ? Number(deal.forecast_contribution ?? 0) / totalForecast
        : null,
    }));
  const dependencyById = new Map(
    dependencyDeals.map(deal => [String(deal.id), deal]),
  );
  const enrichedUrgentDeals = urgentDeals.map(deal => ({
    ...(dependencyById.get(String(deal.id)) ?? deal),
    urgency_bucket: deal.urgency_bucket,
    urgency_score: deal.urgency_score,
  }));
  const managementAttention = buildManagementAttentionShortlist({
    concentrations: [],
    currentSignals: [],
    dependencyDeals,
    movement: null,
    selectedPeriodRange,
    target: null,
    totalForecast,
    urgentDeals: enrichedUrgentDeals,
  });
  const concentrationCandidates = buildPipelineRiskConcentrationCandidates(concentrations);
  const movementCandidate = buildPipelineRiskMovementCandidate(
    movement,
    analysis.exposedOpenDealValue,
  );
  const pipelineRiskCandidates = [
    ...managementAttention.candidates,
    ...concentrationCandidates,
    ...(movementCandidate ? [movementCandidate] : []),
  ];
  const pipelineRiskEligibleCandidates = [
    ...managementAttention.eligibleCandidates,
    ...concentrationCandidates.filter(candidate => candidate.severity !== "informational"),
    ...(movementCandidate && movementCandidate.severity !== "informational"
      ? [movementCandidate]
      : []),
  ];
  const deduplicatedManagement = deduplicateManagementAttentionCandidates(
    pipelineRiskEligibleCandidates,
  );
  const selectedManagementActions = deduplicatedManagement.candidates
    .toSorted((a, b) => (
      managementSeverityRank[a.severity] - managementSeverityRank[b.severity]
      || (b.forecastImpact ?? 0) - (a.forecastImpact ?? 0)
      || a.id.localeCompare(b.id)
    ))
    .slice(0, FORECAST_MANAGEMENT_ATTENTION_CONFIG.maxPriorities);
  const sortedTiming = analysis.timing.toSorted((a, b) => (
    b.rawValue - a.rawValue
    || b.forecastValue - a.forecastValue
  ));
  const dominantTheme = analysis.themes.at(0) ?? null;

  return {
    currentPosition: {
      exposedDealCount: analysis.deals.length,
      exposedForecastShare: analysis.exposedForecastShare,
      exposedForecastValue: analysis.exposedForecastValue,
      exposedOpenDealValue: analysis.exposedOpenDealValue,
      highRiskDealCount: highRisk?.count ?? 0,
      highRiskForecastShare: totalForecast > 0
        ? (highRisk?.forecastValue ?? 0) / totalForecast
        : null,
      highRiskForecastValue: highRisk?.forecastValue ?? 0,
      highRiskOpenDealValue: highRisk?.rawValue ?? 0,
      state: (highRisk?.count ?? 0) > 0
        ? "high_risk_exposure"
        : "no_high_risk_exposure",
    },
    drivers: {
      dominantIndicator: dominantTheme?.indicators.at(0) ?? null,
      dominantTheme,
      state: getDriverState(analysis.themes),
    },
    managementAttention: {
      actions: selectedManagementActions,
      candidates: pipelineRiskCandidates,
      eligibleCandidates: pipelineRiskEligibleCandidates,
      state: selectedManagementActions.length > 1
        ? "multiple_priorities"
        : selectedManagementActions.length === 1
          ? "one_priority"
          : "no_immediate_priority",
      suppressedCandidateIds: Array.from(new Set([
        ...managementAttention.suppressedCandidateIds,
        ...deduplicatedManagement.suppressedCandidateIds,
      ])),
    },
    movement: movement ?? null,
    timing: {
      dominant: sortedTiming.at(0) ?? null,
      state: getTimingState(sortedTiming),
    },
  };
}
